"use client"

import { Col, Row } from "antd"
import TourDetailBreadcrumb from "./TourDetailBreadcrumb"
import Title from "../common/Typography/Title"
import TourDetailImages from "./TourDetailImages"
import TourInputBooking from "./TourInputBooking"
import TourProgram from "./TourProgram"
import TourPlanAndPrice from "./TourPlanAndPrice"
import { useTour } from "@/hooks/tour/useTour"
import TourDetailHighlights from "./TourDetailHighlights"
import TourDetailSectionNav from "./TourDetailSectionNav"
import TourDetailConditionSections from "./TourDetailConditionSections"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import FooterRequestBookingTour from "./FooterRequestBookingTour"
import { useSearchParams } from "next/navigation"
import { useMemo } from "react"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import BookingLayout from "../layouts/BookingLayout"
import BookingResult from "../booking/Result"
import { parseTourName } from "@/utils/tour/parseTourName"
import { tourActions } from "@/lib/redux/features/tour/reduce"
import MKTScript from "../MKTScript"
import { getTourRatingColor } from "@/utils/tour/getTourRatingColor"
import { getTourSocialProofKey, resolveTourRating } from "@/utils/tour/getTourRatingStatus"
import Link from "@/components/common/Typography/Link"
import Text from "@/components/common/Typography/Text"
import { faClock } from "@fortawesome/free-regular-svg-icons"
import { faFileDownload, faFire, faMapMarkedAlt } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useTranslation } from "react-i18next"
import TourCard from "../tours/TourCard"

const refSSR: { current: boolean } = { current: false }

const SOCIAL_PROOF_GUEST_COUNTS: Record<string, number> = {
  "tour.social_proof_15_24h": 15,
  "tour.social_proof_8_8h": 8,
  "tour.social_proof_10_8h": 10,
  "tour.social_proof_20_24h": 20,
  "tour.social_proof_6_4h": 6,
  "tour.social_proof_15_24h_alt": 15,
}

const TourDetail = ({
  tourDetail,
  tourCategories: tourCategoriesResponse,
}: {
  tourDetail: { data?: unknown }
  tourCategories: { data?: { tourCategories?: { id: string; name: string }[] } }
}) => {
  const { tour } = useTour()
  const isDesktop = useIsDesktop()
  const orderDetail = useAppSelector(state => state.booking.order_detail.data || {})
  const step_booking = useAppSelector(state => state.booking.step_booking)
  const relatedTours = useAppSelector(state => state.tour.rencentlyChoosenTours)
  const searchParams = useSearchParams()
  const categoryId = searchParams.get("categoryId")
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  if (!refSSR.current) {
    refSSR.current = true
    dispatch(tourActions.setTourCategoriesData(tourCategoriesResponse))
    dispatch(tourActions.setTourDetailData(tourDetail))
  }

  const tourCategory = useMemo(() => {
    return tourCategoriesResponse?.data?.tourCategories?.find(
      (item) => item.id === categoryId
    )
  }, [categoryId, tourCategoriesResponse?.data?.tourCategories])

  const tourId = tour.data?.id ?? tour.data?.tourId ?? ""
  const rating = useMemo(() => resolveTourRating(tourId), [tourId])
  const ratingColor = getTourRatingColor(rating.score)
  const socialProofKey = useMemo(() => getTourSocialProofKey(tourId), [tourId])
  const tourCode =
    tour.data?.tourId ||
    tour.data?.departureSchedule?.[0]?.tourId ||
    tour.data?.priceCodeManager ||
    ""

  const fileUploads = tour.data?.fileUploads || []
  const pdfFiles = fileUploads.filter(file => file.path?.endsWith(".pdf"))
  const docFiles = fileUploads.filter(file => !file.path?.endsWith(".pdf"))
  const passedFile = pdfFiles.length > 0 ? pdfFiles : docFiles

  if (Object.keys(orderDetail).length > 0 && step_booking === "result") {
    return (
      <BookingLayout>
        <BookingResult needToRefeshData={false} />
      </BookingLayout>
    )
  }

  return (
    <Row justify="center">
      <MKTScript />
      <FooterRequestBookingTour />
      <Col
        span={24}
        className="tour-detail"
        style={isDesktop ? {} : { paddingInline: 0 }}
      >
        <Row style={isDesktop ? {} : { paddingInline: "1.5rem" }}>
          <Col span={24} className="tour-detail__intro">
            <TourDetailBreadcrumb />
            <Title level={2} className="tour-detail__name tour-item__title">
              {tourCategory?.name || ""}{" "}
              {tour.data?.totalDay}N{tour.data?.totalNight}Đ:{" "}
              {parseTourName(tour.data?.name || "")}
            </Title>
            <div className="tour-detail__hero-meta">
              <div className={`tour-item__rating tour-item__rating--${ratingColor}`}>
                <span className="tour-item__rating-score">{rating.score.toFixed(1)}</span>
                <span className="tour-item__rating-label">{t(rating.labelKey)}</span>
                <span className="tour-item__rating-count">
                  {t("tour.reviews_count", { count: rating.reviewCount })}
                </span>
              </div>
              <ul className="tour-item__meta tour-detail__meta tour-detail__meta--inline">
                {!!tour.data?.departure?.name && (
                  <li className="tour-item__meta-item">
                    <FontAwesomeIcon
                      icon={faMapMarkedAlt}
                      className="tour-item__meta-icon"
                      aria-hidden
                    />
                    <span>
                      {t("tour.departure_from_city", { city: tour.data.departure.name })}
                    </span>
                  </li>
                )}
                {!!tourCode && (
                  <li className="tour-item__meta-item tour-detail__meta-tour-code">
                    <span>
                      {t("tour.tour_code")}:{" "}
                      <Text
                        strong
                        ellipsis={{ tooltip: true }}
                        className="tour-detail__tour-code"
                      >
                        {tourCode}
                      </Text>
                    </span>
                  </li>
                )}
                <li className="tour-item__meta-item">
                  <FontAwesomeIcon icon={faClock} className="tour-item__meta-icon" aria-hidden />
                  <span>
                    {tour.data?.totalDay} {t("common.day")} {tour.data?.totalNight}{" "}
                    {t("common.night")}
                  </span>
                </li>
              </ul>
            </div>
            {!!passedFile.length && (
              <Row gutter={12} className="tour-detail__downloads">
                {passedFile.map((file, idx) => (
                  <Col key={file.id}>
                    <Link download href={file.path}>
                      <FontAwesomeIcon icon={faFileDownload} />{" "}
                      {t("tour.tour_detail")} {idx + 1}
                    </Link>
                  </Col>
                ))}
              </Row>
            )}
          </Col>
        </Row>

        <div className="tour-detail__body">
        <Row
          style={{ position: "relative", marginBlock: "1rem" }}
          gutter={[15, 15]}
          wrap={false}
        >
          <Col flex={1} className="tour-detail__main-col">
            <Row gutter={[15, 15]}>
              <Col span={24}>
                <TourDetailImages />
              </Col>
              <Col span={24}>
                <div className="tour-detail__promo-bar">
                  <span className="tour-detail__promo-badge">
                    {tour.data?.labelCampaign || t("tour.promo_best_seller")}
                  </span>
                  <span className="tour-detail__promo-proof">
                    {t("tour.social_proof_booked_prefix")}{" "}
                    <FontAwesomeIcon icon={faFire} className="tour-item__social-proof-icon" aria-hidden />
                    {t(socialProofKey, { count: SOCIAL_PROOF_GUEST_COUNTS[socialProofKey] })}
                  </span>
                </div>
              </Col>
            </Row>
            <div className="tour-detail__sections">
              <TourDetailSectionNav />
              <Row gutter={[15, 15]}>
                <TourDetailHighlights />
                <TourProgram />
                <TourDetailConditionSections
                  bucket="includes"
                  sectionId="tour-section-includes"
                  title={t("tour.price_includes")}
                />
                <TourDetailConditionSections
                  bucket="excludes"
                  sectionId="tour-section-excludes"
                  title={t("tour.price_excludes")}
                />
                <TourDetailConditionSections
                  bucket="terms"
                  sectionId="tour-section-terms"
                  title={t("tour.terms_and_notes")}
                />
                <TourPlanAndPrice />
                {!!relatedTours.length && (
                  <Col id="tour-section-related" span={24}>
                    <div className="tour-detail__section tour-detail__related">
                      <Title level={4} className="tour-detail__section-title">
                        {t("tour.related_tours")}
                      </Title>
                      <Row gutter={[12, 12]}>
                        {relatedTours.slice(0, 3).map((relatedTour) => (
                          <Col key={relatedTour.id} span={isDesktop ? 8 : 24}>
                            <TourCard tour={relatedTour} size="small" />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Col>
                )}
              </Row>
              <div className="tour-detail__section-nav-end" aria-hidden="true" />
            </div>
          </Col>
          {isDesktop && (
            <Col flex="42rem" className="tour-detail__input--info">
              <TourInputBooking />
            </Col>
          )}
        </Row>
        </div>
      </Col>
    </Row>
  )
}

export default TourDetail
